import React from 'react';

import { Text, TextProps } from '@companycam/slab-web';
import { tokens } from '@companycam/slab-tokens';
import ShimmerLoader from 'components/shared/ShimmerLoader/ShimmerLoader';
import { formatDate } from 'helpers/intl-helper';
import styled from 'styled-components';
import useTrialEndQuery from '../choosePlans/gql/useTrialEndQuery';
import { translate, TranslationKey } from 'javascripts/translations/i18n';
import { PaymentMethodFormIdentifier, SubscriptionOption } from '../types';
import SubmitPaymentButton from './SubmitPaymentButton';
import { ANNUAL } from '../Constants';

type TimelineProps = {
  billingLoading: boolean;
  handleSubmit: () => void;
  formId: PaymentMethodFormIdentifier;
  isWalletPayment: boolean;
  paymentSubmitLoading: boolean;
  planTitle: string;
  submitErrorMessage: string;
  selectedSubscription: SubscriptionOption;
  submitPaymentDisabled: boolean;
};

// #region styled-components
const TimelineInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 2;
  margin-left: var(--cc_size_spacing_xl);
  @media (max-width: 1000px) {
    margin-left: 0;
    margin-top: var(--cc_size_spacing_xl);
    padding-left: var(--cc_size_spacing_xs);
    padding-right: var(--cc_size_spacing_xs);
  }
  justify-content: space-between;
`;

const IconContainer = styled.div`
  background-color: var(--cc_color_background_3);
  border-radius: var(--cc_size_border_radius_xxl);
  border: var(--cc_size_border_width_m) solid var(--cc_color_background_3);
  min-width: 40px;
  height: 40px;
  margin-right: var(--cc_size_spacing_m);
  align-content: center;
  align-items: center;
  justify-content: center;
  display: flex;
`;

const Icon = styled.i`
  font-size: var(--cc_size_text_xl);
  color: var(--cc_color_button_text_subtle);
`;

const TimelineTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const TimelineContainer = styled.div`
  justify-content: flex-start;
  margin-bottom: var(--cc_size_spacing_xl);
`;

const SubmitContainer = styled.div`
  margin-top: var(--cc_size_spacing_l);
`;

const TimelineEvent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;

const StyledTrialHeader = styled(Text)<TextProps>`
  span {
    font-weight: 700;
  }
`;

const VerticalLine = styled.div`
  margin-left: 19px;
  margin-top: -10px;
  margin-bottom: -10px;
  background-color: var(--cc_color_background_3);
  width: var(--cc_size_border_width_m);
  height: 48px;
`;
// #endregion styled-components

function Timeline({
  billingLoading,
  handleSubmit,
  formId,
  isWalletPayment,
  paymentSubmitLoading,
  planTitle,
  selectedSubscription,
  submitErrorMessage,
  submitPaymentDisabled,
}: TimelineProps) {
  const { trialEnd, loading } = useTrialEndQuery();
  let trialEndDate = null;
  trialEndDate = new Date(trialEnd);

  const isAnnual = selectedSubscription?.cycle === ANNUAL;
  let priceCopy: TranslationKey =
    'billing.stripePaymentModal.timeline.timelineTrialInfoMonth';
  let price = parseInt(selectedSubscription?.baseMonthlyPrice);
  if (isAnnual) {
    priceCopy = 'billing.stripePaymentModal.timeline.timelineTrialInfoYear';
    price *= 12;
  }

  return (
    <TimelineInfoContainer>
      <TimelineContainer>
        <TimelineEvent>
          <IconContainer>
            <Icon className="mdi mdi-gift-outline" />
          </IconContainer>
          <TimelineTextContainer>
            <Text
              as="p"
              size="s"
              color="subtle"
              family="heading"
              weight={700}
              ccMargin="none none xs none"
            >
              {translate(
                'billing.stripePaymentModal.timeline.timelineHeaderOne',
              )}
            </Text>
            <Text as="p" size="xs" color="subtle" family="heading" weight={400}>
              {translate(
                'billing.stripePaymentModal.timeline.timelineSubHeaderOne',
              )}
            </Text>
          </TimelineTextContainer>
        </TimelineEvent>
        <VerticalLine />
        <TimelineEvent>
          <IconContainer>
            <Icon className="mdi mdi-bell-outline" />
          </IconContainer>
          <TimelineTextContainer>
            <Text
              as="p"
              size="s"
              color="subtle"
              family="heading"
              weight={700}
              ccMargin="none none xs none"
              data-testid="choosePlans__trial-minus-two"
            >
              {translate(
                'billing.stripePaymentModal.timeline.timelineHeaderTwo',
              )}
            </Text>
            <Text as="p" size="xs" color="subtle" family="heading" weight={400}>
              {translate(
                'billing.stripePaymentModal.timeline.timelineSubHeaderTwo',
              )}
            </Text>
          </TimelineTextContainer>
        </TimelineEvent>
        <VerticalLine />
        <TimelineEvent>
          <IconContainer>
            <Icon className="mdi mdi-lightning-bolt" />
          </IconContainer>
          <TimelineTextContainer>
            {loading ? (
              <ShimmerLoader />
            ) : (
              <Text
                as="p"
                size="s"
                color="subtle"
                family="heading"
                weight={700}
                ccMargin="none none xs none"
                data-testid="choosePlans__trial-end"
              >
                {translate(
                  'billing.stripePaymentModal.timeline.timelineHeaderThree',
                )}
              </Text>
            )}
            <Text as="p" size="xs" color="subtle" family="heading" weight={400}>
              {translate(
                'billing.stripePaymentModal.timeline.timelineSubHeaderThree',
              )}{' '}
              {formatDate(trialEndDate, 'MMMM d')}.
            </Text>
          </TimelineTextContainer>
        </TimelineEvent>
      </TimelineContainer>
      <SubmitContainer>
        {billingLoading ? (
          <ShimmerLoader height={tokens.size_text_l} />
        ) : (
          <StyledTrialHeader
            as="p"
            family="heading"
            size="xs"
            ccMargin="none none xxs none"
          >
            {translate(
              'billing.stripePaymentModal.timeline.timelineTrialInfoFirst',
            )}
            <span data-testid="choosePlans__cycle-price">
              {translate(priceCopy, {
                price,
              })}
            </span>
          </StyledTrialHeader>
        )}
        {billingLoading ? (
          <ShimmerLoader
            margin="0 0 var(--cc_size_spacing_s) 0"
            height={tokens.size_text_xl}
          />
        ) : (
          <Text
            as="p"
            size="xs"
            ccMargin="none none l none"
            data-testid="choosePlans__per-user-price"
          >
            {translate(
              'billing.stripePaymentModal.timeline.timelineTrialInfoSubHeader',
              {
                monthlyPricePerUser:
                  selectedSubscription?.monthlyPricePerUser.split('.')[0],
              },
            )}
          </Text>
        )}
        <SubmitPaymentButton
          handleSubmit={handleSubmit}
          formId={formId}
          submitErrorMessage={submitErrorMessage}
          submitPaymentDisabled={submitPaymentDisabled}
          cta={translate('billing.stripePaymentModal.startTrial', {
            planTitle,
          })}
          isWalletPayment={isWalletPayment}
          paymentSubmitLoading={paymentSubmitLoading || !planTitle}
        />
      </SubmitContainer>
    </TimelineInfoContainer>
  );
}

export default Timeline;
